import React from 'react'
import ReactDOM from 'react-dom'
import PropTypes from 'prop-types'
import computeDistanceBetween from './computeDistanceBetween'
import { compactObject } from 'commons/js/util/objectUtils'
import { geocodePlace } from 'commons/js/services/geocoderService'
import isSearchEqual from './isSearchEqual'
import Routes from 'commons/js/routes/routes'
import { searchChargingPoints } from '../../services/chargingPointsServices'
import shouldUpdateUrl from 'commons/js/util/shouldUpdateUrl'
import { makeChargingPointsFilter, getDefaultFilters } from '../../util/chargingPointFilter'
import UrlParams from 'commons/js/util/UrlTo/UrlParams'

function getSearchFromProps ({ placeId }) {
    return compactObject({
        place_id: placeId,
    })
}

class ChargingPointsContainer extends React.Component {
    state = {
        search: {
            place: {
                place_id: this.props.placeId,
            },
        },
        searchPoint: null,
        selectedChargingPointId: null,
        markedChargingPointId: null,
        chargingPoints: [],
        isLoading: false,
        autoCenter: true,
        center: null,
        error: null,
        activeFilter: null,
        filters: getDefaultFilters(),
    }

    setError = this.setError.bind(this)
    setSearch = this.setSearch.bind(this)
    setLoading = this.setLoading.bind(this)
    setSearchPoint = this.setSearchPoint.bind(this)
    setActiveFilter = this.setActiveFilter.bind(this)
    setFilters = this.setFilters.bind(this)
    handleOnChangeMap = this.handleOnChangeMap.bind(this)
    disableAutoCenter = this.disableAutoCenter.bind(this)
    computeDistanceTo = this.computeDistanceTo.bind(this)
    performGeolocation = this.performGeolocation.bind(this)
    handleOnChangeSearchForm = this.handleOnChangeSearchForm.bind(this)
    setChargingPoints = this.setChargingPoints.bind(this)
    selectChargingPoint = this.selectChargingPoint.bind(this)
    receiveChargingPoints = this.receiveChargingPoints.bind(this)
    markChargingPoint = this.markChargingPoint.bind(this)
    markAndCenterChargingPoint = this.markAndCenterChargingPoint.bind(this)

    componentDidMount () {
        this.performGeolocation(getSearchFromProps(this.props))
    }

    componentDidUpdate (prevProps, prevState) {
        const { search, autoCenter } = this.state

        const searchHasChanged = !isSearchEqual(prevState.search, search)
        const autoCenterHasChanged = prevState.autoCenter !== autoCenter

        if (searchHasChanged) {
            this.updateUrl(search)
            this.performSearch(search)
        }

        if (autoCenterHasChanged && prevState.autoCenter) {
            this.disableAutoCenter()
            return
        }

        const { placeId } = prevProps
        if (placeId !== this.props.placeId) {
            this.performGeolocation(getSearchFromProps(this.props))
        }
    }

    updateUrl (search) {
        const locationParams = new UrlParams()
        locationParams.loadFromUrl()
        const searchParams = new UrlParams()
        searchParams.loadFromUrl()
        searchParams.update({place_id: search.place.place_id})

        if (shouldUpdateUrl(locationParams, searchParams)) {
            this.props.push(Routes.chargingPointSearchPath(searchParams.getAll()))
        }
    }

    performSearch (search) {
        this.setLoading(true)
        this.props.searchChargingPoints(search)
            .then(this.receiveChargingPoints)
    }

    performGeolocation (search) {
        const { getPlaceDataFromPlaceIdOrLatLong } = this.props
        getPlaceDataFromPlaceIdOrLatLong({place_id: search.place_id})
            .then((place) => {
                ReactDOM.unstable_batchedUpdates(() => {
                    this.enableAutoCenter()
                    this.setSearchPoint(place.latitude, place.longitude)
                    this.setSearch({ place })
                })
            }, (error) => {
                this.setError(error)
            })
    }

    handleOnChangeSearchForm (searchFormData) {
        this.performGeolocation({place_id: searchFormData.place.place_id})
    }

    handleOnChangeMap (mapData) {
        ReactDOM.unstable_batchedUpdates(() => {
            this.disableAutoCenter()
            this.setSearch({
                place: {
                    ...this.state.search.place,
                    latitude: mapData.latitude,
                    longitude: mapData.longitude,
                    bounds: mapData.bounds,
                },
            })
        })
    }

    receiveChargingPoints ({ canceled, error, result }) {
        if (canceled) {
            return
        }

        ReactDOM.unstable_batchedUpdates(() => {
            this.setLoading(false)
            if (error) {
                this.setError(error)
                return
            }
            this.setError(null)
            this.selectChargingPoint(null)
            this.setChargingPoints(result)
        })
    }

    computeDistanceTo (latitude, longitude) {
        const searchPoint = this.state.searchPoint || {}
        return this.props.computeDistanceBetween(
            {
                latitude: searchPoint.latitude,
                longitude: searchPoint.longitude,
            },
            {
                latitude,
                longitude,
            }
        )
    }

    setSearch (search) {
        this.setState((prevState) => ({
            search: {
                ...prevState.search,
                ...search,
            },
        }))
    }

    selectChargingPoint (selectedChargingPointId) {
        this.setState(() => ({selectedChargingPointId}))
    }

    markAndCenterChargingPoint ({ id, latitude, longitude }) {
        this.setState(() => ({
            markedChargingPointId: id,
            autoCenter: false,
            center: {
                lat: latitude,
                lng: longitude,
            },
        }))
    }

    markChargingPoint (markedChargingPointId) {
        this.setState(() => ({markedChargingPointId, autoCenter: false}))
    }

    setSearchPoint (latitude, longitude) {
        this.setState(() => ({searchPoint: {latitude, longitude}}))
    }

    setChargingPoints (chargingPoints) {
        this.setState(() => ({chargingPoints}))
    }

    setError (error) {
        this.setState(() => ({error}))
    }

    setLoading (isLoading) {
        this.setState(() => ({isLoading}))
    }

    setActiveFilter (activeFilter) {
        this.setState(() => ({activeFilter}))
    }

    setFilters (filters) {
        this.setState((prevState) => ({
            markedChargingPointId: null,
            autoCenter: true,
            activeFilter: null,
            filters: { ...prevState.filters, ...filters },
        }))
    }

    disableAutoCenter () {
        this.setState(() => ({autoCenter: false}))
    }

    enableAutoCenter () {
        this.setState(() => ({autoCenter: true}))
    }

    render () {
        const {
            search,
            chargingPoints,
            selectedChargingPointId,
            markedChargingPointId,
            isLoading,
            autoCenter,
            error,
            center,
            searchPoint,
            activeFilter,
            filters,
        } = this.state

        const filteredChargingPoints = chargingPoints.filter(makeChargingPointsFilter(filters))

        return this.props.children({
            search,
            chargingPoints: filteredChargingPoints,
            selectedChargingPointId,
            markedChargingPointId,
            isLoading,
            autoCenter,
            center,
            error,
            searchPoint,
            filters,
            activeFilter,
            setFormSearch: this.handleOnChangeSearchForm,
            setMapSearch: this.handleOnChangeMap,
            markAndCenterChargingPoint: this.markAndCenterChargingPoint,
            selectChargingPoint: this.selectChargingPoint,
            markChargingPoint: this.markChargingPoint,
            computeDistanceTo: this.computeDistanceTo,
            disableAutoCenter: this.disableAutoCenter,
            setActiveFilter: this.setActiveFilter,
            setFilters: this.setFilters,
        })
    }
}

ChargingPointsContainer.propTypes = {
    getPlaceDataFromPlaceIdOrLatLong: PropTypes.func,
    searchChargingPoints: PropTypes.func,
    computeDistanceBetween: PropTypes.func,
    placeId: PropTypes.string,
    children: PropTypes.func,
    // From connectUrl
    push: PropTypes.func,
}

ChargingPointsContainer.defaultProps = {
    searchChargingPoints: searchChargingPoints,
    getPlaceDataFromPlaceIdOrLatLong: geocodePlace,
    computeDistanceBetween: computeDistanceBetween,
    getChargingPointUrl: () => {},
    push: () => {},
}

export default ChargingPointsContainer
